import {styled} from '@linaria/react';
import React, {ComponentProps, FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {
  desktopAndUpLinaria,
  higherDesktopAndUpLinaria,
  largeScreensLinaria,
  tabletAndUpLinaria,
} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {useGetAriaLabelFromUrl} from '../../../hooks/useGetAriaLabelFromUrl/useGetAriaLabelFromUrl';
import {Button} from '../../atoms/ButtonsDeprecated/Button';
import {tabFocusedStyles} from '../../atoms/TabFocused/tabFocusedStyles';
import {Text} from '../../atoms/Text/Text';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {FullWidthImage} from '../../molecules/FullWidthImage/FullWidthImage';
import {SliderHookResponse} from '../../molecules/HorizontalSlider/types';
import {ProductImage} from '../../molecules/ProductImage/ProductImage';
import {SliderPaginationDots} from '../SliderPaginationDots/SliderPaginationDots';
import {AUTO_SLIDE_DURATION_IN_SECS} from './constants';
import {SlideAmbientProps} from './types';

export const Slide: FC<SlideAmbientProps> = ({
  headline,
  subline,
  productImages,
  floatingChip,
  customSlideImage,
  slideImage,
  isCloseToViewport,
  currentSlide = 0,
  maxPages = 0,
  scrollToSlide,
  slideContentLabels,
  floatingTopLeft,
  floatingTopRight,
  brandLogo,
  isAnimationStopped,
  isAnimationPaused,
  bannerTarget,
  onStopAutomaticSlide,
  uniqueKey,
  linkTargets,
  'data-testid': dataTestid,
  onSlideClick,
}) => {
  const noop = () => {};
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();

  return (
    <Wrapper data-testid={dataTestid} onClick={onSlideClick}>
      <WrapperContent>
        <ContentWrapper>
          {floatingChip}

          {(subline || headline) && (
            <HeadlinesWrapper>
              {subline && <Subline>{subline}</Subline>}
              {headline && <Headline>{headline}</Headline>}
            </HeadlinesWrapper>
          )}
          <Link
            data-testid="bannerTarget"
            aria-label={getAriaLabelFromUrl(bannerTarget.url)}
            to={bannerTarget.url}
            onClick={e => {
              e.preventDefault();
              bannerTarget.onClick?.();
            }}
          />
          <SlidePaginationComponent
            currentSlide={currentSlide}
            maxPages={maxPages}
            scrollToSlide={scrollToSlide ?? noop}
            isAnimationStopped={isAnimationStopped ?? true}
            isAnimationPaused={isAnimationPaused ?? true}
            onStopAutomaticSlide={onStopAutomaticSlide ?? noop}
            key={uniqueKey} // forces the pagination animation to restart
          />
        </ContentWrapper>
        <ProductsWrapper>
          {productImages?.map((productImage, index) => (
            <StyledProductImage
              src={productImage.url}
              widths={[120, 240, 360]}
              key={`spotlight-slider-product-${index}`}
              alt={productImage.description}
              increasePadding={true}
              padded={true}
            />
          ))}
        </ProductsWrapper>
      </WrapperContent>
      <WrapperImage>
        {customSlideImage ? (
          <>{customSlideImage}</>
        ) : (
          slideImage && (
            <StyledFullWidthImage
              dataTestId="browseCardImage"
              imageUrl={slideImage.url}
              description={slideImage.description}
              aspectRatio="2:1"
              highPrioImage={isCloseToViewport}
              disableFixedRatio
              imagePosition="top"
            />
          )
        )}
        {brandLogo && <BrandLogoWrapper>{brandLogo}</BrandLogoWrapper>}

        {(slideContentLabels || linkTargets) && (
          <SlideContentLabelsWrapper
            data-link-target-shown={Boolean(linkTargets)}
          >
            {slideContentLabels}
            {linkTargets && linkTargets.length > 0 && (
              <LinkTargetsWrapper data-testid="categoriesContainer">
                {linkTargets.map(category => (
                  <LinkTarget
                    key={category.label}
                    href={category.linkTarget.url}
                    disableRipple
                    disableShadow
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    {category.label}
                  </LinkTarget>
                ))}
              </LinkTargetsWrapper>
            )}
          </SlideContentLabelsWrapper>
        )}

        <Link
          aria-label={getAriaLabelFromUrl(bannerTarget.url)}
          to={bannerTarget.url}
          onClick={e => {
            e.preventDefault();
            bannerTarget.onClick?.();
          }}
        />

        {(floatingTopRight || floatingTopLeft) && (
          <FloatingTopElements>
            {floatingTopLeft}
            {floatingTopRight}
          </FloatingTopElements>
        )}
      </WrapperImage>
    </Wrapper>
  );
};

interface SlidePaginationComponentProps
  extends ComponentProps<typeof SliderPaginationDots> {
  currentSlide: SliderHookResponse['currentSlide'];
  scrollToSlide: SliderHookResponse['scrollToSlide'];
  onStopAutomaticSlide: VoidFunction;
}

const SlidePaginationComponent: FC<SlidePaginationComponentProps> = ({
  currentSlide,
  scrollToSlide,
  maxPages,
  isAnimationStopped,
  isAnimationPaused,
  onStopAutomaticSlide,
}) => {
  const handleScrollToSlide = (index: number) => {
    onStopAutomaticSlide();
    scrollToSlide(index);
  };

  return (
    <StyledSliderPaginationDots
      isAnimationStopped={isAnimationStopped}
      isAnimationPaused={isAnimationPaused}
      maxPages={maxPages}
      currentSlide={currentSlide}
      scrollToSlide={handleScrollToSlide}
      enlargeActiveBullet={true}
      autoplayDuration={AUTO_SLIDE_DURATION_IN_SECS}
      dotsColor={{
        active: 'rgba(255,255,255,0.4)',
        inactive: 'rgba(255,255,255,0.4)',
        progress: COLORS['Fill.White'],
      }}
    />
  );
};

const Link = styled(UnstyledLinkLinaria)`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const Wrapper = styled.div`
  display: flex;
  height: 100%;
`;

const WrapperContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-basis: 34%;
  width: 34%;
  padding: 32px 30px;
  position: relative;

  ${tabletAndUpLinaria(`
    padding: 16px 20px;
  `)}

  ${desktopAndUpLinaria(`
    padding: 16px 30px;
  `)}
  
  ${higherDesktopAndUpLinaria(`
    padding: 32px 60px;
  `)}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1;
`;

const WrapperImage = styled.div`
  display: flex;
  flex: 1;
  margin: 16px 16px 16px 0;
  position: relative;
`;

const HeadlinesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-block: 24px;

  ${tabletAndUpLinaria(`
    margin: 16px 0 8px 0;
  `)}

  ${largeScreensLinaria(`
    margin-block: 24px;
  `)}
`;

const StyledSliderPaginationDots = styled(SliderPaginationDots)`
  justify-content: start;
  margin: 0;
  position: relative;
  z-index: 3;
`;

const Headline = styled(Text)`
  color: ${COLORS['Text.White']};
  font-size: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.1;
  overflow-wrap: normal;

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px20};
    line-height: 1.2;
  `)}

  ${higherDesktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px32};
    line-height: 1.1;
  `)}

  ${largeScreensLinaria(`font-size: ${FONT_SIZES.px40};`)}
`;

const Subline = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
  color: ${COLORS['Text.White']};
  text-transform: uppercase;
  letter-spacing: 0.64px;

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px10}
  `)}

  ${higherDesktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px14}
  `)}

  ${largeScreensLinaria(`
    font-size: ${FONT_SIZES.px16};
  `)}
`;

const ProductsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 4px;
  margin-top: 24px;
`;

const StyledProductImage = styled(ProductImage)`
  aspect-ratio: 3 / 4;
  height: 100%;
  padding-top: 0;

  &&& img {
    padding: 14% 12%;
  }
`;

const StyledFullWidthImage = styled(FullWidthImage)`
  height: 100%;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0.08%,
      rgba(0, 0, 0, 0.04) 25.61%,
      rgba(0, 0, 0, 0.15) 41.99%,
      rgba(0, 0, 0, 0.4) 96.1%
    );
  }
`;

const SlideContentLabelsWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 66%;
  text-align: center;
  margin: auto;

  &[data-link-target-shown='true'] {
    // link targets are not shown on tablets
    justify-content: center;
    margin-bottom: 0;

    ${desktopAndUpLinaria(`
      justify-content: flex-end;
      margin-bottom: 48px;
    `)}
  }
`;

const FloatingTopElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
`;

const BrandLogoWrapper = styled.div`
  position: absolute;
  bottom: 16px;
  right: 0;
  left: 0;
`;

const LinkTargetsWrapper = styled.div`
  display: none;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 20px;
  pointer-events: all;
  width: 100%;
  z-index: 2;

  ${desktopAndUpLinaria(`
    display: grid;
    margin-top: 40px;
  `)}
`;

const LinkTarget = styled(Button)`
  &:focus-visible {
    ${tabFocusedStyles({
      offset: '2px',
    })}
  }

  & > span {
    width: 100%;
    min-width: 120px;
    height: 100%;
    padding: 11px 16px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid #e5e5e5;
    font-size: ${FONT_SIZES.px12};
    font-weight: ${FONT_WEIGHTS.mediumHeavy};
    line-height: 1.42;
    color: ${COLORS['Text.White']};
    box-shadow: none;
    backdrop-filter: blur(4px);
    align-content: center;

    :hover {
      box-shadow: none;
    }

    ${desktopAndUpLinaria(`
      font-size: ${FONT_SIZES.px14};
    `)}
  }
`;
